import {
  Fragment,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import cx from "classnames";
//@ts-ignore
import { Converter } from "showdown";
import { Message, ToolInvocation } from "ai";
import { translateText } from "../../../services/translation";
import { toast } from "sonner";
import { PiTranslate } from "react-icons/pi";

import styles from "./styles.module.scss";
import { IoSparkles } from "react-icons/io5";
import { Loading, Textarea } from "@geist-ui/core";
import { Check, Info } from "@geist-ui/icons";
import { ArchiveIcon, Pencil1Icon } from "@radix-ui/react-icons";
import { Button, Flex, Tooltip, Separator, Switch } from "@radix-ui/themes";
import { posthogTrackEvent, SimulatorEvents } from "@/assets/posthog";
import { useSimulatorTracking } from "../useSimulatorTracking";
import { MessageSources } from "./messageSources";
import { Text } from "@/components/ui/text";
import * as TooltipShared from "@radix-ui/react-tooltip";
import { Brain } from "lucide-react";

interface Annotation {
  context?: {
    kbContext?: any[];
    ticketsContext?: any[];
    memoriesContext?: any[];
    snippetsContext?: any[];
  };
  messageIndex?: number;
  step?: string;
  description?: { title: string; text: string };
  content?: any;
}
interface IMessage {
  role: "user" | "assistant";
  content: string;
}

export enum Tools {
  SKIP_IT = "skip_it",
  TALK_TO_AN_AGENT = "talk_to_an_agent",
  RESOLVED = "ticket_solved",
  DONT_KNOW = "dont_know",
}

const processMapper = {
  reading_message: "Going over your message",
  looking_for_sources: "Looking for relevant sources in the help center",
  potential_questions: "Searching",
  reviewing_relevant_sources: "Reviewing relevant sources that I found",
  looking_for_the_answer: "Searching the correct answer",
  drafting_response: "Drafting initial Response",
  typing: "Typing...",
  done: "Finished and sent response",
  none: "",
};

export const Messages = ({
  messages,
  isLoading,
  handleEditMessage,
  messagesSources,
  trackingData,
  isLoadingChat,
  isRedrafting = false,
}: {
  messages: Message[];
  handleEditMessage: (message: string) => void;
  isLoading: boolean;
  messagesSources: { [key: string]: any };
  trackingData: { [key: string]: any };
  isLoadingChat: boolean;
  isRedrafting?: boolean;
}) => {
  // PostHog tracking functions
  const { trackSessionStarted } = useSimulatorTracking();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const relevantMessages = useMemo(() => {
    if (!isRedrafting) {
      return messages;
    }
    return messages && Array.isArray(messages) && messages.length > 0
      ? messages.slice(0, -1)
      : [];
  }, [messages, isRedrafting]);

  // handle scrolling down on messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView();
  }, [messages]);

  const steps = useMemo(() => {
    const filteredSteps = (messages?.filter((d: any) => d.step !== "typing") ||
      []) as Annotation[];
    const lastMessageSteps =
      filteredSteps.length > 0
        ? filteredSteps.filter(
            (d: Annotation) =>
              d.messageIndex ===
              filteredSteps[filteredSteps.length - 1]?.messageIndex
          )
        : [];
    return lastMessageSteps;
  }, [messages]);

  return (
    <>
      <>
        {relevantMessages.length === 0 && <div>Analyzing...</div>}
        {relevantMessages.map((m, i) => {
          return (
            <MessageComponent
              role={m.role === "user" ? "user" : "assistant"}
              content={m.content}
              tools={m.toolInvocations}
              isLast={i + 1 === relevantMessages.length}
              handleEditMessage={handleEditMessage}
              sources={m.role === "user" ? null : messagesSources[m.id]}
              trackingData={trackingData}
            />
          );
        })}

        {(isLoading || isLoadingChat) && relevantMessages.length % 2 !== 0 && (
          <MessageComponent
            role={"assistant"}
            content={""}
            isLoading
            handleEditMessage={handleEditMessage}
            sources={[]}
            trackingData={trackingData}
          />
        )}

        <div ref={messagesEndRef} />
      </>
      {/** */}
    </>
  );
};

{
  /* <div className="flex flex-col items-baseline overflow-y-scroll grow h-full max-w-96 p-3">
{(
  steps as {
    step: string;
    description: { title: string; text: string };
    content: any;
  }[]
)
  ?.filter((l) => l.step !== "typing")
  ?.map((annotation, i) => {
    return (
      <TimelineItem
        status="default"
        className="before:content-[''] list-none"
      >
        <TimelineHeading>
          {processMapper[annotation.step as keyof typeof processMapper]}
        </TimelineHeading>
        <TimelineDot
          status={
            i ==
            (steps as any[])?.filter((l) => l.step !== "typing")
              .length -
              1
              ? "current"
              : "done"
          }
        />
        <TimelineLine done />
        <TimelineContent className=" whitespace-break-spaces text-xs">
          {annotation?.description?.title}
          {annotation?.content &&
            Object.keys(annotation.content).length > 0 && (
              <div className="mt-1 text-xs text-gray-500">
                {annotation.step === "reading_sources" && (
                  <div>
                    {" "}
                    <div>Reading:</div>
                    <div>{annotation.description.text}</div>
                  </div>
                )}
                {annotation.step === "reviewing_resources" && (
                  <div>
                    <div>Reviewing:</div>
                    {Object.entries(annotation.content).map(
                      ([sourceType, sources]) => (
                        <div key={sourceType} className="ml-2">
                          <div className="font-medium">
                            {sourceType === "quack-tickets"
                              ? "Tickets:"
                              : sourceType === "quack-kb"
                              ? "Knowledge Base:"
                              : sourceType === "quack-memory"
                              ? "Memories:"
                              : sourceType === "quack-snippet"
                              ? "Snippets:"
                              : sourceType}
                          </div>
                          {Array.isArray(sources) &&
                          sources.length > 0 ? (
                            sources.map((source: any, idx: number) => (
                              <div key={idx} className="ml-2">
                                {source.question ||
                                  source.title ||
                                  source.text ||
                                  `Source ${idx + 1}`}
                              </div>
                            ))
                          ) : (
                            <div className="ml-2">No sources found</div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
        </TimelineContent>
      </TimelineItem>
    );
  })}
<div ref={bottomStepsRef} />
</div> */
}
const AgentWrapper = ({
  children,
  hideIcon = false,
  actionIcon = false,
}: {
  children: ReactElement;
  hideIcon?: boolean;
  actionIcon?: boolean;
}) => {
  return (
    <div className={styles.assistantWrapper}>
      {actionIcon && (
        <div className={styles.actionIcon}>
          <Brain />
        </div>
      )}
      {!hideIcon && (
        <div className={styles.sparklesIcon}>
          <IoSparkles />
        </div>
      )}
      {children}
    </div>
  );
};
const convertor = new Converter({
  simplifiedAutoLink: true,
  excludeTrailingPunctuationFromURLs: true,
  openLinksInNewWindow: true,
  smoothLivePreview: true,
  disableForced4SpacesIndentedSublists: true,
});

const convertMarkdownToHTML = (markdown: string) => {
  if (markdown) {
    // return markdown;
    return convertor?.makeHtml(markdown);
  } else {
    return "";
  }
};

const MessageComponent = ({
  role,
  content,
  tools,
  handleEditMessage,
  isLoading = false,
  isLast = false,
  sources,
  trackingData,
}: IMessage & {
  isLoading?: boolean;
  handleEditMessage: (m: string) => void;
  tools?: ToolInvocation[];
  isLast?: boolean;
  sources: null | any[];
  trackingData: { [key: string]: any };
}) => {
  const { trackSessionStarted } = useSimulatorTracking();
  const [isHover, setHover] = useState(false);
  const [value, setValue] = useState(content);
  const [isEdit, setEdit] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState<string | null>(
    null
  );
  const Wrapper = role === "assistant" ? AgentWrapper : Fragment;

  useEffect(() => {
    setValue(content);
  }, [content]);

  const firstTool = tools?.[0];

  const isResolved =
    firstTool?.state === "result" && firstTool?.result === Tools.RESOLVED;
  const isSkip =
    firstTool?.state === "result" && firstTool?.result === Tools.SKIP_IT;

  const isEscalated =
    firstTool?.state === "result" &&
    (firstTool.result === Tools.TALK_TO_AN_AGENT ||
      firstTool.result === "escalate_with_message");

  const isDontKnow =
    firstTool?.state === "result" && firstTool.result === Tools.DONT_KNOW;

  const OtherTool =
    firstTool?.state === "result" &&
    firstTool.result !== Tools.RESOLVED &&
    firstTool.result !== Tools.SKIP_IT &&
    firstTool.result !== Tools.TALK_TO_AN_AGENT &&
    firstTool.result !== Tools.DONT_KNOW &&
    firstTool.result !== "escalate_with_message";

  const props =
    role === "assistant"
      ? {
          hideIcon:
            isSkip || isEscalated || !content || isResolved || isDontKnow,
        }
      : {};

  return (
    <>
      {OtherTool && (
        <Wrapper {...props} actionIcon hideIcon>
          <div
            className={cx(styles.message, styles["action"], {
              [styles.loading]: isLoading,
              [styles.systemMessage]:
                isSkip || isEscalated || !content || isResolved,
              [styles.editMode]: isEdit,
              [styles.translated]: !!translatedContent,
            })}
            style={{
              maxWidth: "90%",
            }}
          >
            {firstTool?.result?.data?.activity ?? firstTool?.toolName}
          </div>
        </Wrapper>
      )}
      <Wrapper {...props}>
        <>
          <div
            className={cx(styles.message, styles[role], {
              [styles.loading]: isLoading,
              [styles.systemMessage]:
                isSkip || isEscalated || !content || isResolved,
              [styles.editMode]: isEdit,
              [styles.translated]: !!translatedContent,
            })}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onBlur={() => {}}
          >
            {isHover ? (
              <div className={styles.actionBar}>
                <div className="flex items-center gap-2">
                  <Tooltip
                    delayDuration={100}
                    content={
                      translatedContent
                        ? "Show original"
                        : "Translate to English"
                    }
                  >
                    <span>
                      <PiTranslate />
                    </span>
                  </Tooltip>
                  <Switch
                    size="1"
                    disabled={isTranslating}
                    checked={!!translatedContent}
                    onCheckedChange={async (checked) => {
                      if (!checked) {
                        setTranslatedContent(null);
                        return;
                      }
                      setIsTranslating(true);
                      try {
                        const translated = await translateText(content);
                        setTranslatedContent(translated);
                        posthogTrackEvent({
                          event: SimulatorEvents.MESSAGE_SENT,
                          properties: {
                            messageLength: content.length,
                            messageType: 'translated',
                            timestamp: new Date().toISOString(),
                          },
                        });
                      } catch (error) {
                        console.error("Translation failed:", error);
                        toast.error("Failed to translate message");
                      } finally {
                        setIsTranslating(false);
                      }
                    }}
                  />
                </div>
                {role === "assistant" && (
                  <>
                    <Separator orientation={"vertical"} />
                    {sources && sources.length > 0 ? (
                      <Button
                        color="gray"
                        variant="ghost"
                        onClick={() => {
                          setOpen(true);
                          trackSessionStarted('check_sources');
                        }}
                      >
                        Sources <ArchiveIcon />
                      </Button>
                    ) : (
                      <Flex align="center" gap="2">
                        <Text color="gray">No sources</Text>
                        <TooltipShared.Provider delayDuration={100}>
                          <TooltipShared.Root>
                            <TooltipShared.Trigger>
                              <Info className="w-4 h-4" />
                            </TooltipShared.Trigger>
                            <TooltipShared.Portal>
                              <TooltipShared.Content
                                className={styles.tooltipContent}
                                sideOffset={5}
                              >
                                No sources used to generate this message
                                <TooltipShared.Arrow
                                  className={styles.tooltipArrow}
                                />
                              </TooltipShared.Content>
                            </TooltipShared.Portal>
                          </TooltipShared.Root>
                        </TooltipShared.Provider>
                      </Flex>
                    )}
                  </>
                )}
              </div>
            ) : (
              <></>
            )}
            {(isHover || isEdit) && role === "assistant" && isLast && (
              <div
                className={cx(styles.btnContainer, {
                  [styles.editablt]: isEdit,
                })}
              >
                <Button
                  color="gray"
                  variant="ghost"
                  type="button"
                  className={cx(styles.btn)}
                  onClick={() => {
                    const newEditState = !isEdit;
                    setEdit(newEditState);
                    if (newEditState) {
                      trackSessionStarted('enter_edit_mode');
                    }
                  }}
                >
                  {isEdit ? "Cancel" : <Pencil1Icon />}
                </Button>
                {isEdit && (
                  <button
                    className={cx(styles.btn)}
                    onClick={() => {
                      handleEditMessage(value);
                      setEdit(false);
                      posthogTrackEvent({
                        event: SimulatorEvents.MESSAGE_SENT,
                        properties: {
                          messageLength: value.length,
                          messageType: 'edited',
                          timestamp: new Date().toISOString(),
                        },
                      });
                    }}
                  >
                    Save
                  </button>
                )}
              </div>
            )}
            {isLoading ? (
              <Loading width={2.5} />
            ) : (
              <>
                {isSkip && (
                  <div className={styles.system}>
                    <Info />
                    Out of the policy, Quack Agent will skip this question
                  </div>
                )}
                {isEscalated && (
                  <div className={styles.system}>
                    <Info />
                    Quack will escalate it to an agent
                  </div>
                )}
                {isDontKnow && (
                  <div className={styles.system}>
                    <Info />
                    Quack Agent doesn't know the answer
                  </div>
                )}
                {isResolved && (
                  <div className={styles.system}>
                    <Check />
                    Quack Agent has resolved the ticket
                  </div>
                )}
                {(isEscalated || isSkip || isResolved || isDontKnow) && (
                  <pre>
                    Reason:
                    <div className={styles.system}>
                      {firstTool?.args?.explanation}
                    </div>
                  </pre>
                )}

                {isEdit ? (
                  <TextField
                    value={content}
                    index={0}
                    handleBlur={() => {}}
                    handleFocus={() => {}}
                    setExternalValue={setValue}
                    preventSelectAll
                  />
                ) : isSkip || isEscalated || isResolved ? (
                  <></>
                ) : content ? (
                  <div
                    className={styles.messageFormat}
                    dangerouslySetInnerHTML={{
                      __html: convertMarkdownToHTML(
                        translatedContent || content
                      ),
                    }}
                  ></div>
                ) : (
                  <div className={styles.system}>
                    here?
                    {firstTool?.args?.activity ?? ""}
                  </div>
                )}
                {/* <Markdown disallowedElements={["ul"]} unwrapDisallowed>
              {content}
            </Markdown> */}
              </>
            )}
          </div>
          <MessageSources isOpen={isOpen} setOpen={setOpen} sources={sources} />
        </>
      </Wrapper>
    </>
  );
};

// ol/ul - margin top and bottom 0
// li - margin 0 and line height like the font size

const TextField = ({
  index,
  value,
  handleBlur,
  handleFocus,
  setExternalValue,
  disabled = false,
  preventSelectAll = false,
}: {
  index: number;
  value: string;
  setExternalValue?: (v: string) => void;
  handleBlur: (index: number, value: string) => void;
  handleFocus: (index: number) => void;
  disabled?: boolean;
  preventSelectAll?: boolean;
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [v, setValue] = useState(value);
  const [height, setHeight] = useState("unset");

  useEffect(() => {
    setValue(value);

    setTimeout(() => {
      if (textAreaRef.current) {
        setHeight(`${textAreaRef.current.scrollHeight}px`);
      }
    }, 300);
  }, [value]);

  return (
    <Textarea
      ref={textAreaRef}
      onChange={(e) => {
        const inputValue = e.target.value;
        setValue(inputValue);

        if (setExternalValue) {
          setExternalValue(inputValue);
        }
        e.target.style.height = "auto";
        e.target.style.height = `${e.target.scrollHeight}px`;
      }}
      onBlur={(e) => {
        handleBlur(index, e.target.value);
      }}
      onFocus={(e) => {
        handleFocus(index);
        if (!preventSelectAll) {
          e.target.select();
        }
      }}
      value={v}
      disabled={disabled}
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
      style={{
        resize: "none",
        width: "100%",
        overflow: "hidden",
        height: height,
        border: "0",
      }}
    />
  );
};
